/**
 * Centralized configuration for Merchant Search project APIs
 * Manages tokens, endpoints, and global configuration
 */

// Project endpoints configuration
export const ENDPOINTS = {
  // Merchant Service - Management of merchants
  MERCHANTS: {
    base: 'merchant-service/api/v2/merchants',
    list: (size = 10, page = 0) => `?size=${size}&page=${page}`,
    search: '/search',
    filter: '/filter',
    getById: (id) => `/${id}`,
    create: '',
    update: (id) => `/${id}`,
    delete: (id) => `/${id}`,
    addresses: (merchantId) => `/${merchantId}/addresses`,
    banks: (merchantId) => `/${merchantId}/banks`,
    owners: (merchantId) => `/${merchantId}/owners`,
    createBank: (merchantId) => `/${merchantId}/banks`,
    createOwner: (merchantId) => `/${merchantId}/owners`
  },

  // Parameter Service - Catalogues and parameters
  PARAMETERS: {
    base: 'param-service/api/params',
    industries: '/industries?all=true',
    states: '/states?all=true',
    processors: '/processors?all=true',
    businessStructures: '/business_structures?all=true',
    monitoringCentres: '/monitoring_centres'
  },

  // Note Service - Management of notes
  NOTES: {
    base: 'note-service/api/notes',
    getAll: '',
    getByTask: (taskId) => `?task=${taskId}`,
    getByTag: (tag) => `/tag/${tag}`,
    create: '',
    update: (id) => `/${id}`,
    delete: (id) => `/${id}`,
    getById: (id) => `/${id}`,
    softDelete: (id) => `/${id}/status`,
    // New endpoints for advanced search
    search: '/search',
    list: (page = 0, size = 10, sort = 'id,desc') => `?page=${page}&size=${size}&sort=${sort}`,
    searchWithFilters: (filters = {}) => {
      const params = new URLSearchParams();
      
      // Pagination parameters
      if (filters.page !== undefined) params.append('page', filters.page);
      if (filters.size !== undefined) params.append('size', filters.size);
      if (filters.sort !== undefined) params.append('sort', filters.sort);
      
      // Search filters
      if (filters.originType) params.append('originType', filters.originType);
      if (filters.originId) params.append('originId', filters.originId);
      if (filters.tag) params.append('tag', filters.tag);
      if (filters.systemTag) params.append('systemTag', filters.systemTag);
      if (filters.createdBy) params.append('createdBy', filters.createdBy);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.search) params.append('search', filters.search);
      
      const queryString = params.toString();
      return queryString ? `?${queryString}` : '';
    }
  },

  // Application Service - Management of applications
  APPLICATIONS: {
    base: 'application-service/api/v2/applications',
    create: '',
    getById: (id) => `/${id}`,
    update: (id) => `/${id}`,
    process: (applicationId) => `/${applicationId}/process`,
    delete: (id, merchantId) => `/${id}${merchantId !== undefined && merchantId !== null ? `?merchantId=${merchantId}` : ''}`,
    list: (size = 10, page = 0) => `?size=${size}&page=${page}`,
    search: '/search',
    filter: '/filter',
    getAll: '',
    // General search by any value
    generalSearch: (searchValue = '', page = 0, size = 10) => `/generalSearch?searchValue=${encodeURIComponent(searchValue)}&page=${page}&size=${size}`,
    // Filters by status and count
    byStatus: (status, page = 0, size = 10) => `/byStatus?status=${encodeURIComponent(status)}&page=${page}&size=${size}`,
    // Get applications by status in last 24 hours with timezone offset
    byStatusLast24Hours: (status, timezoneOffset, page = 0, size = 10) => `/statuses/${encodeURIComponent(status)}/last-24-hours?timezoneOffset=${encodeURIComponent(timezoneOffset)}&page=${page}&size=${size}`,
    countByStatus: (status) => `/countByStatus?status=${encodeURIComponent(status)}`,
    countByStatusClientTimezone: (status, timezoneOffset) => `/countByStatusClientTimezone?status=${encodeURIComponent(status)}&timezoneOffset=${encodeURIComponent(timezoneOffset)}`,
    // Count applications by status in last 24 hours with timezone offset
    countByStatusLast24Hours: (status, timezoneOffset) => `/statuses/${encodeURIComponent(status)}/last-24-hours?timezoneOffset=${encodeURIComponent(timezoneOffset)}&page=0&size=1`
  },

  // Deal Service - Management of deals
  DEALS: {
    base: 'deal-service/api/deals',
    create: ''
  },

  // Document Service - Management of documents
  DOCUMENTS: {
    base: 'document-service/api/v1/documents',
    upload: '/upload',
    download: (documentId) => `/download?documentId=${documentId}`,
    preview: (documentId) => `/${documentId}/preview`,
    details: (documentId) => `/${documentId}`,
    search: '/search',
    delete: (documentId) => `/${documentId}/delete`,
  },

  // Owner Service - Management of owners (separated from merchants service)
  OWNERS: {
    base: 'merchant-service/api/v2/merchants',
    getById: (merchantId, ownerId) => `/${merchantId}/owners/${ownerId}`,
    contacts: (merchantId, ownerId) => `/${merchantId}/owners/${ownerId}/contacts`,
    addresses: (merchantId, ownerId) => `/${merchantId}/owners/${ownerId}/addresses`
  }
};

/**
 * Configuration of the proxy for development
 */
export const PROXY_CONFIG = {
  // Remote server URL
  baseUrl: 'https://nlf-dev.api.jaduguard.com',
  
  // Local URL (using proxy from package.json)
  localUrl: "/",

  // Specific URL for application service (testing)
  applicationServiceUrl: "http://localhost:8081",

  // Proxy configuration (enabled to use CRA proxy and avoid CORS in dev)
  enabled: true,
  
  // Default headers
  defaultHeaders: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
};

/**
 * Logging configuration for debugging
 */
export const LOGGING_CONFIG = {
  enabled: process.env.NODE_ENV === 'development',
  level: 'info', // 'debug', 'info', 'warn', 'error'
  showRequests: true,
  showResponses: true,
  showErrors: true,
  showTokenInfo: false // Set to true for debugging token issues
};

/**
 * Environment configuration
 */
export const ENVIRONMENT_CONFIG = {
  // Automatically detect environment
  current: process.env.NODE_ENV || 'development',
  
  // URLs por entorno
  environments: {
    development: {
      name: 'Local Development',
      apiUrl: 'http://localhost:8080',
      keycloakUrl: 'http://localhost:9080'
    },
    certification: {
      name: 'Certification Environment',
      apiUrl: 'https://nlf-dev.api.jaduguard.com',
      keycloakUrl: 'https://nlf-dev.api.jaduguard.com/auth'
    },
    production: {
      name: 'Production Environment',
      apiUrl: 'https://api.nl-funding.com',
      keycloakUrl: 'https://api.nl-funding.com/auth/realms/nlf'
    }
  }
};

/**
 * Utility to build endpoint URLs
 * @param {string} service - Service name (MERCHANTS, PARAMETERS, NOTES, APPLICATIONS)
 * @param {string} endpoint - Specific endpoint
 * @returns {string} Complete URL
 */
export const buildEndpointUrl = (service, endpoint = '') => {
  const serviceConfig = ENDPOINTS[service];
  if (!serviceConfig) {
    throw new Error(`Service ${service} not found in ENDPOINTS configuration`);
  }

  const baseEndpoint = serviceConfig.base;
  const fullEndpoint = endpoint ? `${baseEndpoint}${endpoint}` : baseEndpoint;

  return PROXY_CONFIG.enabled
    ? `${PROXY_CONFIG.localUrl}${fullEndpoint}`
    : `${PROXY_CONFIG.baseUrl}/${fullEndpoint}`;
};

const apiConfig = {
  ENDPOINTS,
  PROXY_CONFIG,
  LOGGING_CONFIG,
  ENVIRONMENT_CONFIG,
  buildEndpointUrl,
};

export default apiConfig;
